import { useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Typography,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack, styled, useMediaQuery } from '@mui/system';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ButtonNaked, theme } from '@pagopa/mui-italia';

import { useTranslation } from 'react-i18next';
import { Link as LinkRouter } from 'react-router';

import Building from '../../icons/Building';
import Euro from '../../icons/Euro';
import Leggi from '../../icons/Leggi';
import SEO from '../SEO/Seo';
import { links } from '../../links';

const ShadowBox = styled(Stack)(() => ({
  flex: 1,
  borderRadius: '12px',
  position: 'relative',
  boxShadow: '0px 4px 32px 0px #0E0F131A'
}));

export default function Home() {
  const { t } = useTranslation();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const mainTitleRef = useRef<HTMLHeadingElement>(null);

  // smooth scroll without adding anchor to url
  const handleDiscoverClick = (e: React.MouseEvent) => {
    e.preventDefault();
    mainTitleRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO title={t('home.heroTitle')} description={t('home.heroDesc')} />
      <Box
        bgcolor={'background.paper'}
        color={'primary.contrastText'}
        px={{ xs: 2, md: 0 }}
      >
        <Container maxWidth={'md'}>
          <Breadcrumbs aria-label="breadcrumbs" sx={{ py: 2 }} separator="›">
            <Link
              component={LinkRouter}
              display="flex"
              alignItems="center"
              color={smUp ? 'textSecondary' : 'textPrimary'}
              fontWeight={smUp ? '400' : '600'}
              to={links.public}
              underline="none"
            >
              {!smUp && <ArrowBackIcon style={{ marginRight: 8 }} />}
              Home
            </Link>
            {smUp && (
              <Typography
                color="textPrimary"
                fontWeight={'600'}
                aria-current="page"
              >
                {t('home.title')}
              </Typography>
            )}
          </Breadcrumbs>
          <Stack gap={10}>
            <Stack gap={3}>
              <Stack gap={2}>
                <Typography
                  variant="h2"
                  component={'h1'}
                  lineHeight={{ md: '3.2rem' }}
                >
                  {t('home.heroTitle')}
                </Typography>
                <Typography variant="body1" component={'p'}>
                  {t('home.heroDesc')}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
                <Box>
                  <Button variant="contained" href={links.payNow}>
                    {t('commons.payNow')}
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    aria-label={t('commons.discoverHow')}
                    aria-controls={t('home.mainTitle')}
                    onClick={handleDiscoverClick}
                  >
                    {t('commons.discoverHow')}
                  </Button>
                </Box>
              </Stack>
            </Stack>
            <Stack gap={5}>
              <Stack spacing={2}>
                <Typography variant={'h4'} component={'h2'} ref={mainTitleRef}>
                  {t('home.mainTitle')}
                </Typography>
                <Typography
                  variant={'body1'}
                  component={'p'}
                  color={'textSecondary'}
                >
                  {t('home.mainSubtitle')}
                </Typography>
              </Stack>
              <Stack
                spacing={3}
                direction={{ xs: 'column', md: 'row' }}
                id="mainBoxes"
              >
                <ShadowBox id="mainBoxes-box-0">
                  <Stack gap={3} p={3}>
                    <Box display={'flex'} aria-hidden="true">
                      <Building title="Icona comune" />
                    </Box>
                    <Stack gap={1} alignItems="flex-start">
                      <Typography
                        variant={'body1'}
                        component={'h3'}
                        fontWeight={'600'}
                      >
                        {t('home.mainBoxes.0.title')}
                      </Typography>
                      <Typography
                        variant={'body2'}
                        component={'p'}
                        color={'textSecondary'}
                      >
                        {t('home.mainBoxes.0.desc')}
                      </Typography>
                    </Stack>
                  </Stack>
                </ShadowBox>

                <ShadowBox id="mainBoxes-box-1">
                  <Stack gap={3} p={3}>
                    <Box display={'flex'} aria-hidden="true">
                      <Euro title="Icona euro" />
                    </Box>
                    <Stack gap={1} alignItems="flex-start">
                      <Typography
                        variant={'body1'}
                        component={'h3'}
                        fontWeight={'600'}
                      >
                        {t('home.mainBoxes.1.title')}
                      </Typography>
                      <Typography
                        variant={'body2'}
                        component={'p'}
                        color={'textSecondary'}
                      >
                        {t('home.mainBoxes.1.desc')}
                      </Typography>
                      <ButtonNaked
                        size={'medium'}
                        sx={{ color: 'newBlue.main' }}
                        href={links.payNow}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {t('commons.payNow')}
                      </ButtonNaked>
                    </Stack>
                  </Stack>
                </ShadowBox>

                <ShadowBox id="mainBoxes-box-2">
                  <Stack gap={3} p={3}>
                    <Box display={'flex'} aria-hidden="true">
                      <Leggi title="Icona ricevuta" />
                    </Box>
                    <Stack gap={1} alignItems="flex-start">
                      <Typography
                        variant={'body1'}
                        component={'h3'}
                        fontWeight={'600'}
                      >
                        {t('home.mainBoxes.2.title')}
                      </Typography>
                      <Typography
                        variant={'body2'}
                        component={'p'}
                        color={'textSecondary'}
                      >
                        {t('home.mainBoxes.2.desc')}
                      </Typography>
                      <ButtonNaked
                        component={LinkRouter}
                        to={links.guide}
                        target="_blank"
                        size={'medium'}
                        rel="noopener noreferrer"
                        sx={{ color: 'newBlue.main' }}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {t('home.mainBoxes.2.button')}
                      </ButtonNaked>
                    </Stack>
                  </Stack>
                </ShadowBox>
              </Stack>
            </Stack>
            <Stack
              id="faqBox"
              alignItems={{ xs: 'center', md: 'flex-start' }}
              mb={{ xs: 10, md: 12 }}
            >
              <Typography variant={'h4'} component={'h2'}>
                {t('home.faqBox.title')}
              </Typography>
              <Box>
                <Button
                  component={LinkRouter}
                  to="/faq"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 4 }}
                >
                  {t('home.faqBox.cta')}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
